/**
 * Расчёт приоритета учебного материала.
 *
 * Формула детерминирована и полностью определяется конфигурацией
 * (ТЗ FR-EN-04). Это позволяет объяснить пользователю, почему показано именно
 * это слово, и покрыть выбор тестами.
 */

import type { Hand, MasteryStat, MasteryUnit } from '@type2x/shared-contracts';

import type { LearningConfig, PriorityWeights } from './config/priority.v1.js';
import { clamp01, normalizeRange } from './normalize.js';
import { overdueScore } from './review.js';

/** Слагаемые приоритета — показываются в отладке и объяснении выбора. */
export interface PriorityBreakdown {
  readonly errorScore: number;
  readonly hesitationScore: number;
  readonly realWorldFrequency: number;
  readonly spacedRepetitionDue: number;
  readonly transferGap: number;
  readonly handBalanceNeed: number;
}

/** Итог расчёта приоритета одной единицы. */
export interface PriorityResult {
  readonly unit: MasteryUnit;
  readonly priority: number;
  readonly breakdown: PriorityBreakdown;
}

/** Контекст, общий для всех единиц текущего расчёта. */
export interface PriorityContext {
  /** Текущий момент в миллисекундах календарного времени. */
  readonly nowMs: number;
  /** Доля нагрузки на левую руку за последние сессии, 0..1. */
  readonly leftHandShare: number;
  /** Рука, которой набирается единица. */
  readonly handOf: (unit: MasteryUnit) => Hand | null;
  /** Встречалась ли единица в контрольном материале. */
  readonly seenInControl: (unit: MasteryUnit) => boolean;
}

/** Идеальная доля нагрузки на одну руку. */
const BALANCED_SHARE = 0.5;

/** Отклонение баланса, при котором потребность в выравнивании максимальна. */
const MAX_IMBALANCE = 0.25;

function handBalanceNeed(unit: MasteryUnit, context: PriorityContext): number {
  const hand = context.handOf(unit);
  if (hand === null) return 0;

  const share = hand === 'left' ? context.leftHandShare : 1 - context.leftHandShare;
  // Недогруженная рука нуждается в материале; перегруженная — нет.
  const deficit = BALANCED_SHARE - share;
  return clamp01(deficit / MAX_IMBALANCE);
}

/**
 * Разрыв переноса: единица освоена на тренировке, но не подтверждена на
 * незнакомом материале (ТЗ FR-EN-09, §7.6).
 *
 * Здесь же половина Transfer Guard: тренировочная и контрольная статистика
 * разделены (`seenInControl`), а пересечение наборов ограничивает сам
 * генератор — контрольный пул берётся из хвоста частотного списка.
 */
function transferGap(unit: MasteryUnit, stat: MasteryStat, context: PriorityContext): number {
  if (context.seenInControl(unit)) return 0;
  return clamp01(stat.masteryScore);
}

function hesitationScore(stat: MasteryStat, config: LearningConfig): number {
  return normalizeRange(
    stat.medianLatencyMs,
    config.hesitationThresholdMs,
    config.hesitationCeilingMs,
  );
}

/** Сумма весов конфигурации — используется для проверки её корректности. */
export function weightsSum(weights: PriorityWeights): number {
  return Object.values(weights).reduce((sum, weight) => sum + weight, 0);
}

/** Рассчитывает приоритет одной единицы. */
export function computePriority(
  unit: MasteryUnit,
  stat: MasteryStat,
  config: LearningConfig,
  context: PriorityContext,
): PriorityResult {
  const breakdown: PriorityBreakdown = {
    errorScore: clamp01(stat.errorRate),
    hesitationScore: hesitationScore(stat, config),
    realWorldFrequency: clamp01(unit.realWorldFrequency),
    spacedRepetitionDue: overdueScore(stat, context.nowMs),
    transferGap: transferGap(unit, stat, context),
    handBalanceNeed: handBalanceNeed(unit, context),
  };

  const { weights } = config;
  const priority =
    breakdown.errorScore * weights.errorScore +
    breakdown.hesitationScore * weights.hesitationScore +
    breakdown.realWorldFrequency * weights.realWorldFrequency +
    breakdown.spacedRepetitionDue * weights.spacedRepetitionDue +
    breakdown.transferGap * weights.transferGap +
    breakdown.handBalanceNeed * weights.handBalanceNeed;

  return { unit, priority: clamp01(priority), breakdown };
}

/**
 * Сортирует единицы по убыванию приоритета.
 *
 * При равном приоритете порядок определяется идентификатором единицы: иначе
 * результат зависел бы от порядка вставки и генератор перестал бы быть
 * детерминированным.
 */
export function rankUnits(results: readonly PriorityResult[]): PriorityResult[] {
  return [...results].sort((a, b) => {
    if (b.priority !== a.priority) return b.priority - a.priority;
    return a.unit.id.localeCompare(b.unit.id);
  });
}
